<script setup lang="ts">
/**
 * The scrolling transcript.
 *
 * Auto-scrolls on append **unless** the reader has scrolled up, in which case it
 * holds position and offers a jump-to-latest. Yanking the viewport away from
 * someone mid-read is the single most common chat-UI defect and the reason the
 * lock exists.
 *
 * Not virtualized. The specced component list asks for it, but chat bubbles are
 * variable-height (a message may carry a collapsed reasoning block, a tool card,
 * an image), and windowing variable heights well needs a real dependency —
 * which would then own the scroll anchoring this component's whole job is to get
 * right. Retention bounds a conversation's length, so the DOM cost is bounded
 * too. Revisit with a measured problem rather than a presumed one.
 */
import { nextTick, ref, watch } from 'vue';

import type { ChatCapabilities } from '../types/transport';
import type { ChatMessage } from '../types/message';
import MessageBubble from './MessageBubble.vue';

const props = defineProps<{
  messages: readonly ChatMessage[];
  capabilities: ChatCapabilities;
  busy?: boolean;
  traceable?: boolean;
  /** Collapses tool-call cards out of the transcript (reader preference). */
  hideToolCalls?: boolean;
}>();

const emit = defineEmits<{
  approve: [messageId: string];
  deny: [messageId: string];
  'submit-result': [messageId: string, output: string, isError: boolean];
  trace: [taskId: string];
}>();

const scroller = ref<HTMLElement | null>(null);
const pinned = ref(true);

/** Within a few px of the bottom counts as "at the bottom" — exact equality
 *  fails on fractional scroll heights and zoom. */
const NEAR_BOTTOM_PX = 48;

function onScroll() {
  const el = scroller.value;
  if (!el) return;
  pinned.value = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
}

async function scrollToBottom(smooth = false) {
  await nextTick();
  const el = scroller.value;
  if (!el) return;
  el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  pinned.value = true;
}

// Track both append and in-place growth: a streaming message mutates its own
// content without changing the array length.
watch(
  () => [props.messages.length, props.messages[props.messages.length - 1]?.content.length],
  () => {
    if (pinned.value) void scrollToBottom();
  },
  { flush: 'post' },
);

defineExpose({ scrollToBottom });
</script>

<template>
  <div class="relative flex-1 overflow-hidden">
    <div
      ref="scroller"
      data-testid="message-list"
      class="h-full space-y-4 overflow-y-auto px-4 py-4"
      @scroll.passive="onScroll"
    >
      <slot name="empty" />

      <!-- Streaming text is announced politely so a screen reader narrates the
           answer as it arrives without interrupting the reader. -->
      <div aria-live="polite" aria-relevant="additions text" class="space-y-4">
        <MessageBubble
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :capabilities="capabilities"
          :busy="busy"
          :traceable="traceable"
          :hide-tool-calls="hideToolCalls"
          @approve="emit('approve', message.id)"
          @deny="emit('deny', message.id)"
          @submit-result="(o, e) => emit('submit-result', message.id, o, e)"
          @trace="(taskId) => emit('trace', taskId)"
        />
      </div>
    </div>

    <button
      v-if="!pinned"
      type="button"
      data-testid="jump-to-latest"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-800"
      @click="scrollToBottom(true)"
    >
      Jump to latest ↓
    </button>
  </div>
</template>
